import React from "react";
import { Badge } from "@ncoder/ui";
import { cn } from "@/lib/utils";
import { BucketBadge } from "./BucketBadge";
import type { FindingOut } from "@/services/salesforce-release";

interface Props {
  finding: FindingOut;
  isLast?: boolean;
}

export const ImpactFindingDetailRow: React.FC<Props> = ({ finding, isLast }) => {
  const lines = finding.lines && finding.lines !== "n/a" ? finding.lines : null;
  const sym = finding.deprecated_or_changed_symbol || null;
  const repl = finding.replacement || null;
  const section =
    finding.evidence?.find((e) => e.section_path)?.section_path ?? null;
  const releaseSnippet =
    finding.evidence?.find((e) => e.chunk_snippet)?.chunk_snippet ?? null;

  return (
    <div
      className={cn(
        "flex flex-col gap-2 py-3 px-4 pl-14 transition-colors hover:bg-muted/30",
        !isLast && "border-b border-dashed border-muted"
      )}
    >
      {/* Top row: bucket + confidence + lines */}
      <div className="flex flex-wrap items-center gap-2">
        <BucketBadge bucket={finding.bucket} />
        <Badge variant="outline" className="text-xs">
          {finding.confidence}
        </Badge>
        {lines ? (
          <Badge variant="outline" className="font-mono text-xs">
            L{lines}
          </Badge>
        ) : null}
        {section ? (
          <span className="text-xs text-muted-foreground truncate">
            · from release section: <span className="font-medium">{section}</span>
          </span>
        ) : null}
      </div>

      {/* Symbol → replacement */}
      {(sym || repl) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded border border-border bg-muted/20 p-3">
          {sym && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Changed / deprecated symbol
              </p>
              <code className="block font-mono text-xs text-foreground/90 break-all">
                {sym}
              </code>
            </div>
          )}
          {repl && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Suggested replacement
              </p>
              <code className="block font-mono text-xs text-foreground/90 break-all">
                {repl}
              </code>
            </div>
          )}
        </div>
      )}

      {/* Rationale */}
      {finding.rationale ? (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
            Rationale
          </p>
          <p className="text-sm text-foreground/85">{finding.rationale}</p>
        </div>
      ) : null}

      {/* Fix action */}
      {finding.fix_action ? (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
            Fix
          </p>
          <p className="text-sm text-foreground/85">{finding.fix_action}</p>
        </div>
      ) : null}

      {/* Release-note evidence */}
      {releaseSnippet ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Release-note evidence
          </summary>
          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-card p-2 text-foreground/70">
            {releaseSnippet}
          </pre>
        </details>
      ) : null}
    </div>
  );
};

export default ImpactFindingDetailRow;
